import type { QueueEntry } from "./queue-manager";
import type { ScheduledJob } from "./scheduler-backend";
import { executeWithProvider, type PublishDeps } from "./scheduler-publish";
import type { ResolvedSchedulerConfig } from "./types/scheduler";

/** Interval for immediate processing (1 second) */
const IMMEDIATE_INTERVAL_MS = 1000;

export interface PublishScheduleRunnerDeps {
  config: ResolvedSchedulerConfig;
  getPublishDeps: () => PublishDeps;
  isRunning: () => boolean;
}

/**
 * Handles publish queue scheduling and execution.
 * ContentScheduler owns the public API and delegates queue processing here.
 */
export class PublishScheduleRunner {
  private readonly deps: PublishScheduleRunnerDeps;
  private publishJobs: Map<string, ScheduledJob> = new Map();
  private immediateIntervalJob: ScheduledJob | null = null;

  constructor(deps: PublishScheduleRunnerDeps) {
    this.deps = deps;
  }

  public start(): void {
    for (const [entityType, cronExpr] of Object.entries(this.entitySchedules)) {
      const job = this.deps.config.backend.scheduleCron(cronExpr, () =>
        this.processEntityType(entityType),
      );
      this.publishJobs.set(entityType, job);
    }

    this.immediateIntervalJob = this.deps.config.backend.scheduleInterval(
      IMMEDIATE_INTERVAL_MS,
      () => this.processUnscheduledTypes(),
    );
  }

  public async stop(): Promise<void> {
    const jobs = Array.from(this.publishJobs.values());
    this.publishJobs.clear();

    if (this.immediateIntervalJob) {
      jobs.push(this.immediateIntervalJob);
      this.immediateIntervalJob = null;
    }

    await Promise.all(jobs.map((job) => job.stop()));
  }

  private async processEntityType(entityType: string): Promise<void> {
    if (!this.deps.isRunning()) return;

    try {
      const next = await this.deps.config.queueManager.getNext(entityType);
      if (next) {
        await this.processEntry(next);
      }
    } catch (error) {
      this.deps.config.logger.error(
        `Scheduler error for ${entityType}:`,
        error,
      );
    }
  }

  private async processUnscheduledTypes(): Promise<void> {
    if (!this.deps.isRunning()) return;

    try {
      const queuedTypes =
        await this.deps.config.queueManager.getQueuedEntityTypes();

      for (const entityType of queuedTypes) {
        if (!this.entitySchedules[entityType]) {
          const next = await this.deps.config.queueManager.getNext(entityType);
          if (next) {
            await this.processEntry(next);
            break;
          }
        }
      }
    } catch (error) {
      this.deps.config.logger.error(
        "Scheduler error for unscheduled types:",
        error,
      );
    }
  }

  private async processEntry(entry: QueueEntry): Promise<void> {
    await this.deps.config.queueManager.remove(
      entry.entityType,
      entry.entityId,
    );

    const publishDeps = this.deps.getPublishDeps();
    await executeWithProvider(entry, publishDeps);
  }

  private get entitySchedules(): Record<string, string> {
    return this.deps.config.entitySchedules;
  }
}
